/* eslint-disable unicorn/no-null */
import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { UnstyledButton } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
// components
import { DateRangeSelect } from '@newo/ui-kit';
// utils
import { TChannel } from '@/types/common.ts';
import ChannelByIntegrationIdnSelect from '@/components/ChannelByIntegrationIdnSelect';
import {
    channelsToIntegrations,
    integrationsToChannels,
    localDateToUTC,
    utcDateToLocal
} from '@/utils/filters';
import { DATE_FORMAT } from '@/constants';
import useContactsStore from '@/pages/Contacts/Contacts.store';

interface FiltersProps {
    total: number;
}

const Filters = ({ total }: FiltersProps) => {
    const { filters, setFilters, dateRange, setDateRange, resetFilters } =
        useContactsStore(
            useShallow((state) => ({
                filters: state.filters,
                setFilters: state.setFilters,
                dateRange: state.dateRange,
                setDateRange: state.setDateRange,
                resetFilters: state.resetFilters
            }))
        );

    const handleChannelChange = (channels: TChannel[]) => {
        setFilters({ connectors: channelsToIntegrations(channels) });
    };

    const handleFromDateTimeChange = (value: Date | null) => {
        if (value) {
            setDateRange(undefined);
        }
        setFilters({
            from_datetime: value ? localDateToUTC(value) : undefined
        });
    };

    const handleToDateTimeChange = (value: Date | null) => {
        if (value) {
            setDateRange(undefined);
        }
        setFilters({
            to_datetime: value ? localDateToUTC(value) : undefined
        });
    };

    const handleDateTimeChange = (value: string | null) => {
        setDateRange(value ?? undefined);
        handleFromDateTimeChange(null);
        handleToDateTimeChange(null);
    };

    const handleClear = () => {
        resetFilters();
    };

    return (
        <aside className="flex flex-col gap-4">
            <div className="flex justify-between text-xs">
                <span>
                    Found {total} contact{total === 1 ? '' : 's'}
                </span>
                <UnstyledButton
                    className="text-xs text-blue-6"
                    onClick={handleClear}
                >
                    Clear
                </UnstyledButton>
            </div>
            <div className="flex flex-col gap-4 pb-20">
                <ChannelByIntegrationIdnSelect
                    channels={integrationsToChannels(filters.connectors ?? [])}
                    onChange={handleChannelChange}
                />
                <DateRangeSelect
                    size="xs"
                    value={dateRange ?? null}
                    onChange={handleDateTimeChange}
                />
                <DateTimePicker
                    clearable
                    label="From datetime"
                    placeholder="Pick date and time"
                    size="xs"
                    timeInputProps={{
                        withSeconds: false
                    }}
                    value={
                        filters.from_datetime
                            ? utcDateToLocal(filters.from_datetime)
                            : null
                    }
                    valueFormat={DATE_FORMAT.SHORT_24H}
                    onChange={handleFromDateTimeChange}
                />
                <DateTimePicker
                    clearable
                    label="To datetime"
                    placeholder="Pick date and time"
                    size="xs"
                    timeInputProps={{
                        withSeconds: false
                    }}
                    value={
                        filters.to_datetime
                            ? utcDateToLocal(filters.to_datetime)
                            : null
                    }
                    valueFormat={DATE_FORMAT.SHORT_24H}
                    onChange={handleToDateTimeChange}
                />
            </div>
        </aside>
    );
};

export default memo(Filters);
